'use client';

import { BaseItem } from '@algolia/autocomplete-core';
import { autocomplete, AutocompleteOptions } from '@algolia/autocomplete-js';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { Box } from '@chakra-ui/react';
import React, {
  createElement,
  Fragment,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { createRoot, Root } from 'react-dom/client';
import { usePagination, useSearchBox } from 'react-instantsearch';

import { useIndexSearch } from '@/hooks/filters/index-search.hook';
import { debouncePromise } from '@/utils/general';

import '@algolia/autocomplete-theme-classic';
import './Autocomplete.scss';

type Props = Partial<AutocompleteOptions<BaseItem>> & {
  onSearch?: (query: string) => void;
};

const Autocomplete = ({ onSearch, ...autocompleteProps }: Props) => {
  const autocompleteContainer = useRef<HTMLDivElement>(null);
  const panelRootRef = useRef<Root | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);

  const { refine: setQuery } = useSearchBox();
  const { refine: setPage } = usePagination();
  const { indexQuery, setIndexQuery, clearIndexQuery } = useIndexSearch();

  useEffect(() => {
    if (indexQuery.query != '' && onSearch) {
      onSearch(indexQuery.query);
    }

    // set search box query
    setQuery(indexQuery.query);
    setPage(0);
  }, [indexQuery]);

  const plugins = useMemo(() => {
    const recentSearches = createLocalStorageRecentSearchesPlugin({
      key: 'instantsearch',
      limit: 5,
      transformSource({ source }) {
        return {
          ...source,
          onSelect({ item }) {
            setIndexQuery({ query: item.label });
          },
        };
      },
    });

    return [recentSearches];
  }, []);

  useEffect(() => {
    if (!autocompleteContainer.current) {
      return;
    }

    const debouncedSetInstantSearchUiState = debouncePromise(
      setIndexQuery,
      500,
    );

    const autocompleteInstance = autocomplete({
      ...autocompleteProps,
      container: autocompleteContainer.current,
      initialState: indexQuery,
      onReset() {
        clearIndexQuery();
      },
      onSubmit({ state }) {
        setIndexQuery({ query: state.query });
      },
      onStateChange({ prevState, state }) {
        if (prevState.query !== state.query) {
          debouncedSetInstantSearchUiState({ query: state.query });
        }
      },
      renderer: { createElement, Fragment, render: () => {} },
      render({ children }, root) {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;

          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }

        panelRootRef.current.render(children);
      },
      plugins,
    });

    return () => autocompleteInstance.destroy();
  }, [plugins, indexQuery]);

  return <Box ref={autocompleteContainer} width={'full'} />;
};

export default Autocomplete;
